import { useEffect, useState, useMemo } from "react";
import { api, fmtINR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Search } from "lucide-react";

export default function Inventory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 100;

  useEffect(() => {
    setLoading(true);
    api.get("/inventory")
      .then(({ data }) => setRows(data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return rows.filter(r => !q || r.sku.toLowerCase().includes(q.toLowerCase()) || r.name.toLowerCase().includes(q.toLowerCase()));
  }, [rows, q]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const paginatedItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  useEffect(() => { setPage(1); }, [q]);

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500">Live Inventory</div>
        <h1 className="mt-1 text-3xl font-medium tracking-tight" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>Inventory</h1>
        <p className="text-sm text-slate-600 mt-1">Live stock records from SAP Business One.</p>
      </div>
      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Search SKU or item..." value={q} onChange={e=>setQ(e.target.value)} className="pl-9" data-testid="inventory-search" />
      </div>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">SKU</th>
              <th className="text-left px-4 py-3">Item</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-right px-4 py-3">Physical Stock</th>
              <th className="text-right px-4 py-3">Blocked</th>
              <th className="text-right px-4 py-3">Available</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              [...Array(6)].map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td className="px-4 py-3"><Skeleton className="h-4 w-20" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-40" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-20" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-12 ml-auto" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-12 ml-auto" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-5 w-14 ml-auto" /></td>
                </tr>
              ))
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-16 text-slate-500">No items match.</td></tr>
            ) : (
              paginatedItems.map(r => {
                const avail = r.available_stock !== undefined ? r.available_stock : r.stock;
                const blocked = r.blocked_stock || 0;
                return (
                  <tr key={r.sku} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs">{r.sku}</td>
                    <td className="px-4 py-3">{r.name}</td>
                    <td className="px-4 py-3 text-slate-500 capitalize">{r.category}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-600">{r.stock}</td>
                    <td className="px-4 py-3 text-right font-mono text-amber-600">{blocked > 0 ? `-${blocked}` : "0"}</td>
                    <td className="px-4 py-3 text-right">
                      <Badge variant="outline" className={avail > 30 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : avail > 0 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-rose-50 text-rose-700 border-rose-200"}>
                        {avail}
                      </Badge>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
        {filtered.length > PAGE_SIZE && (
          <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50">
            <div className="text-xs text-slate-500">
              Showing <span className="font-medium">{(page - 1) * PAGE_SIZE + 1}</span> to <span className="font-medium">{Math.min(page * PAGE_SIZE, filtered.length)}</span> of <span className="font-medium">{filtered.length}</span> items (100 per page)
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="h-8 text-xs">Previous</Button>
              <span className="text-xs text-slate-600 px-2 font-medium">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="h-8 text-xs">Next</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
